import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { APP_SETTINGS_ID } from '../lib/constants';

interface UseSettingsManagementReturn {
  photoAlbumUnlockDate: Date;
  loading: boolean;
  error: string | null;
  unlockPhotoAlbum: () => Promise<void>;
  lockPhotoAlbum: () => Promise<void>;
  refreshSettings: () => Promise<void>;
}

export function useSettingsManagement(): UseSettingsManagementReturn {
  const [photoAlbumUnlockDate, setPhotoAlbumUnlockDate] = useState<Date>(new Date('2025-08-02T00:00:00Z'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('app_settings')
        .select('photo_album_unlock_date')
        .eq('id', APP_SETTINGS_ID)
        .single();
        
      if (fetchError) throw fetchError;
      
      if (data?.photo_album_unlock_date) {
        setPhotoAlbumUnlockDate(new Date(data.photo_album_unlock_date));
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const unlockPhotoAlbum = async () => {
    try {
      const { error: updateError } = await supabase
        .from('app_settings')
        .update({ photo_album_unlock_date: new Date().toISOString() })
        .eq('id', APP_SETTINGS_ID);
        
      if (updateError) throw updateError;
      
      setPhotoAlbumUnlockDate(new Date());
      setError(null);
    } catch (err) {
      console.error('Error unlocking photo album:', err);
      throw new Error('Failed to unlock photo album');
    }
  };

  const lockPhotoAlbum = async () => {
    try {
      const defaultDate = '2025-08-02T00:00:00Z';
      const { error: updateError } = await supabase
        .from('app_settings')
        .update({ photo_album_unlock_date: defaultDate })
        .eq('id', APP_SETTINGS_ID);
        
      if (updateError) throw updateError;
      
      setPhotoAlbumUnlockDate(new Date(defaultDate));
      setError(null);
    } catch (err) {
      console.error('Error locking photo album:', err);
      throw new Error('Failed to lock photo album');
    }
  };

  return {
    photoAlbumUnlockDate,
    loading,
    error,
    unlockPhotoAlbum,
    lockPhotoAlbum,
    refreshSettings: fetchSettings
  };
}